/**
 * AISections - Embeddable AI question sections component
 *
 * Displays predefined questions that clients can click to trigger the Rose widget.
 * The section collapses when the widget isn't loaded and expands with animation when ready.
 *
 * @example
 * ```tsx
 * // With inline config
 * <AISections
 *   config={{
 *     title: 'Ask us about pricing',
 *     questions: ['What plan fits my needs?', 'Is there a free trial?'],
 *   }}
 *   onQuestionClick={(q) => console.log('Clicked:', q)}
 * />
 *
 * // With section ID (fetches from database)
 * <AISections sectionId="pricing-faq" />
 * ```
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Analytics } from '../../analytics';
import { getSearchBarPlaceholder } from '../../config/constants';
import { useWidgetReadiness } from '../../hooks/widget/useWidgetReadiness';
import { logger } from '../../utils/core/logger';
import { scopeCSS } from '../../utils/cssScoper';
import { validateCSS } from '../../utils/cssValidator';
import { AISectionsButton } from './AISectionsButton';
import { AISectionsSearchBar } from './AISectionsSearchBar';

/**
 * Localized string for multi-language support
 */
export type LocalizedString = {
    en?: string;
    fr?: string;
    de?: string;
    es?: string;
    it?: string;
};

/**
 * Configuration for an AI Section
 */
export interface AISectionConfig {
    /** Title displayed above the question buttons */
    title: string;
    /** Array of question strings to display */
    questions: string[];
    /** Custom styling options */
    styling?: AISectionStyling;
    /** Raw CSS for custom theming (scoped to .ai-sections-container) */
    customCSS?: string;
    /** Whether to show a search bar for custom questions */
    showSearchBar?: boolean;
    /** Localized placeholder text for the search bar (uses main bar placeholder if not set) */
    searchBarPlaceholder?: LocalizedString;
    /** When true, hides the main widget search bar while this section's search bar is visible */
    hidesMainBar?: boolean;
    /** Optional context prepended only to predefined button questions sent to the AI (not shown in chat bubble) */
    context?: string;
}

/**
 * Styling options for AI Sections
 */
export interface AISectionStyling {
    /** Primary/accent color (used for icons) */
    primaryColor?: string;
    /** Background color for the section container */
    backgroundColor?: string;
    /** Background color for buttons */
    buttonBackground?: string;
    /** Text color for buttons */
    buttonTextColor?: string;
    /** Font size for button text (CSS value) */
    buttonFontSize?: string;
    /** Font weight for button text (CSS value) */
    buttonFontWeight?: string;
    /** Size of sparkle icon in pixels */
    iconSize?: number;
    /** Size of arrow icon in pixels */
    arrowSize?: number;
    /** Title text color */
    titleColor?: string;
    /** Gap between buttons (CSS value) */
    gap?: string;
    /** Layout mode: 'inline' (wrapped) or 'stacked' (one per line) */
    layout?: 'inline' | 'stacked';
    /** Horizontal alignment of buttons: 'left', 'center', or 'right' (default: 'left') */
    alignment?: 'left' | 'center' | 'right';
    /** Hide AI Sections on mobile devices (default: false) */
    disableOnMobile?: boolean;
    /** Mobile breakpoint in pixels for disableOnMobile (default: 768) */
    mobileBreakpoint?: number;
    /** Position of search bar relative to question buttons (default: 'bottom') */
    searchBarPosition?: 'top' | 'bottom';
    /** When enabled, automatically shows the first hidden parent when the section is active, and re-hides it when inactive (default: false) */
    unhideParent?: boolean;
    /** CSS z-index of the section container (default: 10). Lower it when the section overlaps a low-z-index fixed nav/menu on the host page. */
    zIndex?: number;
}

export interface AISectionsProps {
    /** Section ID to fetch config from database */
    sectionId?: string;
    /** Inline configuration (used if sectionId not provided or as fallback) */
    config?: AISectionConfig;
    /** Whether to show shimmer effect on first reveal */
    showShimmer?: boolean;
    /** Delay in ms between button animations (default: 100) */
    staggerDelay?: number;
    /** Timeout in ms before collapsing if widget fails to load (default: 10000) */
    collapseTimeout?: number;
    /** Callback when a question is clicked */
    onQuestionClick?: (question: string) => void;
    /** Callback when section expands */
    onExpand?: () => void;
    /** Callback when section collapses */
    onCollapse?: () => void;
    /** Custom class name for the container */
    className?: string;
    /** Language code for localization (default: 'en') */
    language?: string;
    /** Site name for placeholder text (used when no custom placeholder is set) */
    siteName?: string;
}

// Default styling
const DEFAULT_STYLING: Required<AISectionStyling> = {
    primaryColor: '#006967',
    backgroundColor: 'transparent',
    buttonBackground: '#FFFFFF',
    buttonTextColor: '#1a1a1a',
    buttonFontSize: '20px',
    buttonFontWeight: '400',
    iconSize: 20,
    arrowSize: 24,
    titleColor: '#003E3F',
    gap: '12px',
    layout: 'stacked',
    alignment: 'left',
    disableOnMobile: false,
    mobileBreakpoint: 768,
    searchBarPosition: 'bottom',
    unhideParent: false,
    zIndex: 10,
};

// Built-in responsive CSS - always injected, no customCSS required
// This ensures the component works out of the box on all screen sizes
const RESPONSIVE_CSS = `
    /* AI Sections - Built-in responsive defaults */
    .ai-sections-content {
        padding: 24px 32px;
    }

    @media (max-width: 768px) {
        .ai-sections-content {
            padding: 16px 20px;
        }
        /* Prevent embedding website CSS from overriding flex direction */
        .ai-sections-content > div {
            flex-direction: column !important;
        }
        .ai-sections-button {
            padding: 8px 12px;
        }
        .ai-sections-button-text {
            font-size: 16px !important;
        }
        .ai-sections-title {
            font-size: 16px !important;
            margin-bottom: 12px !important;
        }
        .ai-sections-search-bar {
            padding: 8px 12px;
        }
        .ai-sections-search-input {
            font-size: 16px !important;
        }
    }

    @media (max-width: 480px) {
        .ai-sections-content {
            padding: 12px 16px;
        }
        .ai-sections-button-text {
            font-size: 14px !important;
        }
        .ai-sections-title {
            font-size: 14px !important;
        }
        .ai-sections-search-input {
            font-size: 14px !important;
        }
    }
`;

export function AISections({
    sectionId: _sectionId, // Reserved for future database fetching
    config,
    showShimmer = true,
    staggerDelay = 100,
    collapseTimeout = 10000,
    onQuestionClick,
    onExpand,
    onCollapse,
    className = '',
    language = 'en',
    siteName,
}: AISectionsProps) {
    const { isReady, isLoading, error, triggerQuestion } = useWidgetReadiness({
        timeout: collapseTimeout,
    });

    const [isExpanded, setIsExpanded] = useState(false);
    const [hasExpandedOnce, setHasExpandedOnce] = useState(false);
    const [isMobileHidden, setIsMobileHidden] = useState(false);

    // Refs to hold latest callback values - prevents effect re-runs when parent
    // provides unstable callback references (not wrapped in useCallback)
    const onExpandRef = useRef(onExpand);
    const onCollapseRef = useRef(onCollapse);
    onExpandRef.current = onExpand;
    onCollapseRef.current = onCollapse;

    // Ref for section content visibility detection (used by hidesMainBar feature)
    const sectionContentRef = useRef<HTMLDivElement>(null);
    const searchBarWrapperRef = useRef<HTMLDivElement>(null);

    // Use provided config or empty defaults
    const effectiveConfig = config || { title: '', questions: [] };
    // Merge styling with defaults, filtering out undefined values from config
    // to ensure defaults are used when styling properties are not explicitly set
    // Note: primaryColor from config already includes site color fallback (set by aiSectionsService)
    const configStyling = effectiveConfig.styling || {};
    const styling = {
        ...DEFAULT_STYLING,
        ...Object.fromEntries(
            Object.entries(configStyling).filter(([, value]) => value !== undefined)
        ),
    } as Required<AISectionStyling>;

    // Track which properties were explicitly set in styling
    // Priority: styling prop (explicit) > customCSS > default
    const explicitProps = new Set(
        Object.entries(configStyling)
            .filter(([, value]) => value !== undefined)
            .map(([key]) => key)
    );

    const hasCustomCSS = Boolean(effectiveConfig.customCSS);

    // Helper: should we use inline style for this property?
    // Yes if: explicitly set in styling, OR no customCSS to override
    const shouldUseInlineStyle = (prop: keyof AISectionStyling) =>
        explicitProps.has(prop) || !hasCustomCSS;

    // Generate unique scope ID - use sectionId if provided for clarity, otherwise fallback to React's useId
    // Sanitize to CSS-safe characters (alphanumeric, underscore, hyphen)
    const reactId = useId().replace(/:/g, '');
    const scopeId = (_sectionId || reactId).replace(/[^a-zA-Z0-9_-]/g, '-');
    const styleId = `ai-sections-${scopeId}`;

    // Mobile detection for disableOnMobile feature
    // Only runs resize listener when disableOnMobile is true to avoid unnecessary overhead
    useEffect(() => {
        if (!styling.disableOnMobile) {
            setIsMobileHidden(false);
            return;
        }

        const breakpoint = styling.mobileBreakpoint;
        const checkMobile = () => {
            const isMobile = window.innerWidth < breakpoint;
            setIsMobileHidden(isMobile);
            if (isMobile) {
                logger.debug('AISections: Hidden on mobile', {
                    width: window.innerWidth,
                    breakpoint,
                });
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [styling.disableOnMobile, styling.mobileBreakpoint]);

    // Inject responsive CSS (always) + base styles + customCSS into document
    // Responsive CSS ensures component works out of the box on all screen sizes
    // Base styles use CSS variables with defaults, customCSS can override
    useEffect(() => {
        const responsiveStyleId = `${styleId}-responsive`;
        const baseStyleId = `${styleId}-base`;
        const customStyleId = `${styleId}-custom`;

        // Always inject responsive CSS - this is the "works out of the box" principle
        let responsiveStyleEl = document.getElementById(
            responsiveStyleId
        ) as HTMLStyleElement | null;
        if (!responsiveStyleEl) {
            responsiveStyleEl = document.createElement('style');
            responsiveStyleEl.id = responsiveStyleId;
            document.head.appendChild(responsiveStyleEl);
        }
        responsiveStyleEl.textContent = scopeCSS(
            RESPONSIVE_CSS,
            `.ai-sections-${scopeId}`,
            '.ai-sections-container'
        );

        // Only inject base + custom styles when customCSS is present
        if (hasCustomCSS) {
            // Validate customCSS before injection
            if (effectiveConfig.customCSS) {
                const validation = validateCSS(effectiveConfig.customCSS);
                if (!validation.isValid) {
                    logger.error('AISections: Invalid customCSS detected', {
                        errors: validation.errors,
                        parsedRuleCount: validation.parsedRuleCount,
                        expectedRuleCount: validation.expectedRuleCount,
                    });
                }
                if (validation.warnings.length > 0) {
                    logger.warn('AISections: customCSS warnings', {
                        warnings: validation.warnings,
                    });
                }
            }

            // Create base styles that use CSS variables - these have low specificity so customCSS can override
            // These are scoped to this instance to prevent conflicts between multiple AI sections
            const baseCSS = scopeCSS(
                `
                .ai-sections-container {
                    background-color: var(--ai-sections-bg);
                }
                .ai-sections-title {
                    color: var(--ai-sections-title-color);
                }
            `,
                `.ai-sections-${scopeId}`,
                '.ai-sections-container'
            );

            // Inject base styles first
            let baseStyleEl = document.getElementById(baseStyleId) as HTMLStyleElement | null;
            if (!baseStyleEl) {
                baseStyleEl = document.createElement('style');
                baseStyleEl.id = baseStyleId;
                document.head.appendChild(baseStyleEl);
            }
            baseStyleEl.textContent = baseCSS;

            // Inject custom styles after (higher priority)
            let customStyleEl = document.getElementById(customStyleId) as HTMLStyleElement | null;
            if (!customStyleEl) {
                customStyleEl = document.createElement('style');
                customStyleEl.id = customStyleId;
                document.head.appendChild(customStyleEl);
            }
            customStyleEl.textContent = scopeCSS(
                effectiveConfig.customCSS || '',
                `.ai-sections-${scopeId}`,
                '.ai-sections-container'
            );
            logger.debug('AISections: Injected responsive + base + custom CSS', {
                responsiveStyleId,
                baseStyleId,
                customStyleId,
            });
        } else {
            logger.debug('AISections: Injected responsive CSS', { responsiveStyleId });
        }

        return () => {
            // Cleanup on unmount
            document.getElementById(responsiveStyleId)?.remove();
            document.getElementById(baseStyleId)?.remove();
            document.getElementById(customStyleId)?.remove();
        };
    }, [hasCustomCSS, effectiveConfig.customCSS, styleId, scopeId]);

    // Handle expansion/collapse based on widget readiness
    // This effect tracks visibility changes - expanding when ready, collapsing when not
    // Uses refs for callbacks to prevent re-runs when parent provides unstable references
    useEffect(() => {
        if (isReady && !isExpanded) {
            logger.debug('AISections: Widget ready, expanding section');
            setIsExpanded(true);
            if (!hasExpandedOnce) {
                setHasExpandedOnce(true);
                // Track AI Section displayed on first expansion
                Analytics.trackAISectionDisplayed({
                    questionCount: effectiveConfig.questions.length,
                    questions: effectiveConfig.questions,
                    sectionId: _sectionId,
                    hasSearchBar: effectiveConfig.showSearchBar,
                    language,
                });
            }
            onExpandRef.current?.();
        } else if (!isReady && isExpanded) {
            logger.debug('AISections: Widget not ready, collapsing section', { error });
            setIsExpanded(false);
            onCollapseRef.current?.();
        }
    }, [
        isReady,
        isExpanded,
        hasExpandedOnce,
        error,
        _sectionId,
        language,
        effectiveConfig.questions,
        effectiveConfig.showSearchBar,
    ]);

    // IntersectionObserver for hidesMainBar feature
    // When enabled, emits window events when this section's content enters/exits viewport
    // Main widget listens to these events to hide/show its own search bar
    // biome-ignore lint/correctness/useExhaustiveDependencies: isExpanded is an intentional trigger — re-observe DOM when expanded state changes
    useEffect(() => {
        const sectionContentEl = sectionContentRef.current;
        const shouldObserve = effectiveConfig.hidesMainBar && sectionContentEl;

        if (!shouldObserve) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                const eventName = entry.isIntersecting
                    ? 'inboundx_section_searchbar_entered'
                    : 'inboundx_section_searchbar_exited';
                window.dispatchEvent(new CustomEvent(eventName));
                logger.debug(`AISections: Search bar visibility changed`, {
                    isIntersecting: entry.isIntersecting,
                    event: eventName,
                });
            },
            { threshold: 0.2 } // Main bar reveals when hero is ~80% hidden
        );

        observer.observe(sectionContentEl);

        // Note: No manual initial emission needed - IntersectionObserver
        // fires its callback immediately when .observe() is called

        return () => {
            observer.disconnect();
            // Notify that search bar is gone when unmounting
            window.dispatchEvent(new CustomEvent('inboundx_section_searchbar_exited'));
        };
    }, [effectiveConfig.hidesMainBar, isExpanded]);

    // Handle question from button or search bar
    const handleQuestion = useCallback(
        (question: string, source: 'button' | 'search_bar') => {
            logger.debug('AISections: Question triggered', { question, source });

            // Track the AI Section click in PostHog
            Analytics.trackAISectionClicked({
                questionText: question,
                source,
                sectionId: _sectionId,
                language,
            });

            // Trigger the question in the widget with granular source and section context
            const widgetSource =
                source === 'button' ? 'ai_section_button' : 'ai_section_search_bar';
            const questionContext = source === 'button' ? effectiveConfig.context : undefined;
            const success = triggerQuestion(question, widgetSource, questionContext);

            if (success) {
                onQuestionClick?.(question);
            } else {
                logger.warn('AISections: Failed to trigger question in widget');
            }
        },
        [triggerQuestion, onQuestionClick, _sectionId, language, effectiveConfig.context]
    );

    const handleButtonClick = useCallback(
        (question: string) => handleQuestion(question, 'button'),
        [handleQuestion]
    );

    const handleSearchBarSubmit = useCallback(
        (question: string) => handleQuestion(question, 'search_bar'),
        [handleQuestion]
    );

    // Compute search bar placeholder:
    // 1. If custom placeholder exists, use it with language fallback to English
    // 2. Otherwise, use main widget bar placeholder
    // Always replace {{company_name}} if present
    const rawPlaceholder = effectiveConfig.showSearchBar
        ? effectiveConfig.searchBarPlaceholder?.[language as keyof LocalizedString] ||
          effectiveConfig.searchBarPlaceholder?.en ||
          getSearchBarPlaceholder('COLLAPSED', language, siteName)
        : '';
    const searchBarPlaceholder =
        siteName && rawPlaceholder.includes('{{company_name}}')
            ? rawPlaceholder.replace('{{company_name}}', siteName)
            : rawPlaceholder;

    // Don't render if hidden on mobile (disableOnMobile is true and viewport < mobileBreakpoint)
    if (isMobileHidden) {
        return null;
    }

    // Don't render if no questions AND no search bar
    const hasQuestions = effectiveConfig.questions && effectiveConfig.questions.length > 0;
    const hasSearchBar = effectiveConfig.showSearchBar;
    if (!hasQuestions && !hasSearchBar) {
        logger.debug('AISections: No questions or search bar configured, not rendering');
        return null;
    }

    // Don't render if still loading and never expanded (initial state)
    // This prevents flash of collapsed content
    if (isLoading && !hasExpandedOnce) {
        return null;
    }

    // CSS custom properties for theming - allows customCSS to override specific values
    const cssVars = {
        '--ai-sections-bg': styling.backgroundColor,
        '--ai-sections-title-color': styling.titleColor,
        '--ai-sections-primary-color': styling.primaryColor,
        '--ai-sections-button-bg': styling.buttonBackground,
        '--ai-sections-button-text': styling.buttonTextColor,
        '--ai-sections-gap': styling.gap,
    } as React.CSSProperties;

    return (
        <div
            className={`ai-sections-container ai-sections-${scopeId} overflow-hidden ${className}`}
            style={{
                // Only set CSS vars as defaults - customCSS can override everything
                ...cssVars,
                maxHeight: isExpanded ? '600px' : '0px',
                opacity: isExpanded ? 1 : 0,
                // Use inline style if explicitly set in styling, otherwise let customCSS win
                ...(shouldUseInlineStyle('backgroundColor')
                    ? { backgroundColor: styling.backgroundColor }
                    : {}),
                borderRadius: '16px',
                transition: 'all 0.5s ease-out',
                // Ensure section is above decorative background images (Figma/Webflow).
                // Configurable per section to drop below low-z-index fixed navs/menus.
                position: 'relative',
                zIndex: styling.zIndex,
            }}
        >
            <div className="ai-sections-content">
                {/* Title */}
                {effectiveConfig.title && (
                    <h3
                        className="ai-sections-title"
                        style={{
                            fontWeight: 600,
                            fontSize: '18px',
                            marginBottom: '16px',
                            transition: 'opacity 0.3s',
                            // Use inline style if explicitly set in styling, otherwise let customCSS win
                            ...(shouldUseInlineStyle('titleColor')
                                ? { color: styling.titleColor }
                                : {}),
                            opacity: isExpanded ? 1 : 0,
                        }}
                    >
                        {effectiveConfig.title}
                    </h3>
                )}

                {/* Question buttons and search bar */}
                <div
                    ref={sectionContentRef}
                    style={{
                        display: 'flex',
                        flexDirection: styling.layout === 'stacked' ? 'column' : 'row',
                        ...(styling.layout === 'stacked'
                            ? {
                                  alignItems:
                                      styling.alignment === 'center'
                                          ? 'center'
                                          : styling.alignment === 'right'
                                            ? 'flex-end'
                                            : 'flex-start',
                              }
                            : {
                                  flexWrap: 'wrap' as const,
                                  justifyContent:
                                      styling.alignment === 'center'
                                          ? 'center'
                                          : styling.alignment === 'right'
                                            ? 'flex-end'
                                            : 'flex-start',
                              }),
                        gap: styling.gap,
                        width: '100%',
                    }}
                >
                    {/* Search bar at top position */}
                    {effectiveConfig.showSearchBar && styling.searchBarPosition === 'top' && (
                        <div
                            ref={searchBarWrapperRef}
                            style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent:
                                    styling.alignment === 'center'
                                        ? 'center'
                                        : styling.alignment === 'right'
                                          ? 'flex-end'
                                          : 'flex-start',
                            }}
                        >
                            <AISectionsSearchBar
                                placeholder={searchBarPlaceholder}
                                primaryColor={styling.primaryColor}
                                backgroundColor={styling.buttonBackground}
                                textColor={styling.buttonTextColor}
                                fontSize={styling.buttonFontSize}
                                fontWeight={styling.buttonFontWeight}
                                iconSize={styling.iconSize}
                                arrowSize={styling.arrowSize}
                                show={isExpanded}
                                animationDelay={0}
                                showShimmer={showShimmer}
                                useInlineBackground={shouldUseInlineStyle('buttonBackground')}
                                useInlineTextColor={shouldUseInlineStyle('buttonTextColor')}
                                useInlineFontSize={shouldUseInlineStyle('buttonFontSize')}
                                useInlineFontWeight={shouldUseInlineStyle('buttonFontWeight')}
                                isStandalone={!hasQuestions}
                                onSubmit={handleSearchBarSubmit}
                            />
                        </div>
                    )}

                    {/* Question buttons */}
                    {effectiveConfig.questions.map((question, questionIndex) => {
                        // Adjust animation delay based on search bar position
                        const baseDelay = styling.searchBarPosition === 'top' ? staggerDelay : 0;
                        return (
                            <AISectionsButton
                                key={question}
                                question={question}
                                primaryColor={styling.primaryColor}
                                backgroundColor={styling.buttonBackground}
                                textColor={styling.buttonTextColor}
                                fontSize={styling.buttonFontSize}
                                fontWeight={styling.buttonFontWeight}
                                iconSize={styling.iconSize}
                                arrowSize={styling.arrowSize}
                                show={isExpanded}
                                animationDelay={baseDelay + questionIndex * staggerDelay}
                                showShimmer={showShimmer}
                                useInlineBackground={shouldUseInlineStyle('buttonBackground')}
                                useInlineTextColor={shouldUseInlineStyle('buttonTextColor')}
                                useInlineFontSize={shouldUseInlineStyle('buttonFontSize')}
                                useInlineFontWeight={shouldUseInlineStyle('buttonFontWeight')}
                                onClick={handleButtonClick}
                            />
                        );
                    })}

                    {/* Search bar at bottom position (default) */}
                    {effectiveConfig.showSearchBar && styling.searchBarPosition !== 'top' && (
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent:
                                    styling.alignment === 'center'
                                        ? 'center'
                                        : styling.alignment === 'right'
                                          ? 'flex-end'
                                          : 'flex-start',
                            }}
                        >
                            <AISectionsSearchBar
                                placeholder={searchBarPlaceholder}
                                primaryColor={styling.primaryColor}
                                backgroundColor={styling.buttonBackground}
                                textColor={styling.buttonTextColor}
                                fontSize={styling.buttonFontSize}
                                fontWeight={styling.buttonFontWeight}
                                iconSize={styling.iconSize}
                                arrowSize={styling.arrowSize}
                                show={isExpanded}
                                animationDelay={effectiveConfig.questions.length * staggerDelay}
                                showShimmer={showShimmer}
                                useInlineBackground={shouldUseInlineStyle('buttonBackground')}
                                useInlineTextColor={shouldUseInlineStyle('buttonTextColor')}
                                useInlineFontSize={shouldUseInlineStyle('buttonFontSize')}
                                useInlineFontWeight={shouldUseInlineStyle('buttonFontWeight')}
                                isStandalone={!hasQuestions}
                                onSubmit={handleSearchBarSubmit}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
